import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { CheckCircle2, Loader2, XCircle, KeyRound } from "lucide-react";

import { Header } from "@/components/Header";
import logo from "@/assets/logo.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getFirebaseAuth } from "@/lib/firebase/config";

const searchSchema = z.object({
  mode: z.string().optional(),
  oobCode: z.string().optional(),
  apiKey: z.string().optional(),
  lang: z.string().optional(),
  continueUrl: z.string().optional(),
});

export const Route = createFileRoute("/auth/action")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Account Action — Real Pranks" },
      { name: "description", content: "Complete your account action." },
    ],
  }),
  component: AuthActionPage,
});

type ActionState =
  | { status: "loading" }
  | { status: "success"; message: string }
  | { status: "error"; message: string }
  | { status: "resetPassword"; email: string };

function AuthActionPage() {
  const { mode, oobCode } = Route.useSearch();
  const [state, setState] = useState<ActionState>({ status: "loading" });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!mode || !oobCode) {
      setState({ status: "error", message: "Invalid or missing action link." });
      return;
    }

    const auth = getFirebaseAuth();

    switch (mode) {
      case "verifyEmail":
        applyActionCode(auth, oobCode)
          .then(() =>
            setState({
              status: "success",
              message: "Your email has been verified. You're all set!",
            }),
          )
          .catch(() =>
            setState({
              status: "error",
              message:
                "This verification link is invalid or has expired. Try requesting a new one from your account settings.",
            }),
          );
        break;

      case "verifyAndChangeEmail":
        applyActionCode(auth, oobCode)
          .then(() =>
            setState({
              status: "success",
              message:
                "Your email address has been updated and verified. You can sign in with your new email.",
            }),
          )
          .catch(() =>
            setState({
              status: "error",
              message:
                "This email change link is invalid or has expired. Try requesting the change again from your account settings.",
            }),
          );
        break;

      case "recoverEmail":
        checkActionCode(auth, oobCode)
          .then((info) =>
            applyActionCode(auth, oobCode).then(() =>
              setState({
                status: "success",
                message: `Your email has been reverted to ${info.data.email ?? "your previous address"}. If you didn't request this change, consider resetting your password.`,
              }),
            ),
          )
          .catch(() =>
            setState({
              status: "error",
              message:
                "This recovery link is invalid or has expired. Contact support if you need help.",
            }),
          );
        break;

      case "resetPassword":
        verifyPasswordResetCode(auth, oobCode)
          .then((email) => setState({ status: "resetPassword", email }))
          .catch(() =>
            setState({
              status: "error",
              message:
                "This password reset link is invalid or has expired. Try requesting a new one.",
            }),
          );
        break;

      default:
        setState({ status: "error", message: `Unknown action: "${mode}".` });
    }
  }, [mode, oobCode]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setState({
        status: "error",
        message: "Password must be at least 8 characters.",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      setState({ status: "error", message: "Passwords don't match." });
      return;
    }
    setResetting(true);
    try {
      await confirmPasswordReset(getFirebaseAuth(), oobCode!, newPassword);
      setState({
        status: "success",
        message: "Your password has been reset. You can now sign in with your new password.",
      });
    } catch {
      setState({
        status: "error",
        message: "Failed to reset your password. The link may have expired — try requesting a new one.",
      });
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="mx-auto flex w-full max-w-[92%] flex-col gap-6 px-4 py-8 sm:max-w-md">
        <img src={logo} alt="" className="mx-auto h-16 w-auto pt-4" />

        {state.status === "loading" && (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Processing…</p>
          </div>
        )}

        {state.status === "success" && (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Done!</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {state.message}
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" asChild>
                <Link to="/">Go home</Link>
              </Button>
              <Button asChild>
                <Link to="/account">My account</Link>
              </Button>
            </div>
          </div>
        )}

        {state.status === "error" && (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Something went wrong</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {state.message}
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" asChild>
                <Link to="/">Go home</Link>
              </Button>
              <Button asChild>
                <Link to="/login">Sign in</Link>
              </Button>
            </div>
          </div>
        )}

        {state.status === "resetPassword" && (
          <div className="flex flex-col gap-5 rounded-xl border border-border bg-card p-6">
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                <KeyRound className="h-5 w-5" />
              </div>
              <h1 className="text-lg font-bold">Reset your password</h1>
              <p className="text-sm text-muted-foreground">
                Enter a new password for{" "}
                <span className="font-medium text-foreground">
                  {state.email}
                </span>
                .
              </p>
            </div>
            <form onSubmit={handlePasswordReset} className="flex flex-col gap-4">
              <div className="space-y-1.5">
                <label
                  htmlFor="new-password"
                  className="text-sm font-medium text-foreground"
                >
                  New password
                </label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoFocus
                  minLength={8}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="confirm-password"
                  className="text-sm font-medium text-foreground"
                >
                  Confirm password
                </label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Re-enter your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </div>
              <Button type="submit" disabled={resetting} className="h-11">
                {resetting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Reset password"
                )}
              </Button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
